import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            toast.success(t("auth.login_subtitle"));
            const dest = res.user.role === "admin" ? "/admin" : "/tools";
            navigate(dest, { replace: true });
        } else {
            toast.error(res.error);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="login-page">
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFFFFF] via-[#FAFAFA] to-[#EFF6FF]" />
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: `url(https://images.pexels.com/photos/6422928/pexels-photo-6422928.jpeg)`,
                        backgroundSize: "cover", backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-[#FAFAFA]/60" />
                <div className="relative flex flex-col justify-between p-12 text-zinc-950 z-10">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src="/social-factory-logo.png"
                            alt="Social Factory"
                            className="h-12 w-12 object-contain invert"
                        />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Social Factory</div>
                            <div className="text-sm font-bold">{t("common.brand_studio")}</div>
                        </div>
                    </Link>
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] mb-4">{t("auth.login_title")}</div>
                        <h2 className="font-display font-black text-4xl tracking-tighter leading-none max-w-md text-balance">
                            {t("landing.hero_title_1")}
                            <br />
                            <span className="italic font-medium text-[#E5A93D]">{t("landing.hero_title_2")}</span>
                        </h2>
                        <p className="text-zinc-500 mt-4 max-w-md text-sm leading-relaxed">
                            {t("landing.hero_subtitle")}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 relative">
                <Link to="/" className="absolute top-6 left-6 text-zinc-500 hover:text-zinc-950 text-sm flex items-center gap-2" data-testid="back-home">
                    <ArrowLeft className="h-4 w-4" /> {t("common.back")}
                </Link>
                <div className="absolute top-6 right-6">
                    <LanguageSwitcher variant="compact" />
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full max-w-md"
                >
                    <div className="mb-10">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] mb-3">{t("auth.login_title")}</div>
                        <h1 className="font-display font-black text-4xl tracking-tighter">{t("auth.login_subtitle")}</h1>
                    </div>
                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.email")}</Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="u@makelaardij.be"
                                className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("auth.password")}</Label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] focus-visible:ring-[#E5A93D]/20 h-11"
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold tracking-wide rounded-sm active:scale-[0.98]"
                            data-testid="login-submit-btn"
                        >
                            {loading ? t("tools.buying") : t("auth.login_button")}
                        </Button>
                    </form>
                    <div className="mt-6 text-sm text-zinc-500 text-center">
                        {t("auth.no_account")}{" "}
                        <Link to="/register" className="text-[#E5A93D] hover:underline" data-testid="to-register-link">
                            {t("auth.register_link")}
                        </Link>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
